import { useRef, useState } from 'react';
import { Card, Field, Pill, useToast } from '../../../components/ui';
import { banner as bannerApi, type BannerAppearance } from '../../../lib/api';
import { AA_FLOOR, contrastRatio, isHex } from '../../../lib/contrast';
import { bytes } from '../../../lib/format';
import { FONT_LABEL, LOOK_LABEL, RADIUS_LABEL, type CardProps } from './defaults';

type Look = BannerAppearance['look'];
type ColourKey = keyof typeof LOOK_LABEL;

const LOGO_LIMIT = 50 * 1024;

/** Colour picker beside a hex field: the picker for the eye, the field for a brand guide. */
function Colour({ label, value, readOnly, onChange, children }: {
  label: string; value: string; readOnly: boolean; onChange: (v: string) => void; children?: React.ReactNode;
}) {
  const ok = isHex(value);
  return (
    <Field label={label}>
      <div className="ck-colour">
        <input type="color" value={ok ? value : '#000000'} disabled={readOnly} aria-label={`${label} picker`} onChange={(e) => onChange(e.target.value.toUpperCase())} />
        <input
          type="text" className={`mono${ok ? '' : ' invalid'}`} value={value} maxLength={7} readOnly={readOnly} aria-label={label}
          onChange={(e) => {
            let v = e.target.value.trim();
            if (v && !v.startsWith('#')) v = `#${v}`;
            onChange(v.toUpperCase());
          }}
        />
      </div>
      {children}
    </Field>
  );
}

function Contrast({ fg, bg, against }: { fg: string; bg: string; against: string }) {
  if (!isHex(fg) || !isHex(bg)) return null;
  const ratio = contrastRatio(fg, bg);
  return (
    <div className="ck-contrast">
      <span>Contrast {ratio.toFixed(2)} · {against}</span>
      {ratio < AA_FLOOR && <Pill tone="warn">Below {AA_FLOOR}</Pill>}
    </div>
  );
}

export default function LookCard({ a, update, readOnly, logoUrl, onLogo }: CardProps & {
  logoUrl: string | null;
  onLogo: (url: string | null) => void;
}) {
  const toast = useToast();
  const look = a.look;
  const set = (patch: Partial<Look>) => update((x) => ({ ...x, look: { ...x.look, ...patch } }));
  const colour = (k: ColourKey) => (v: string) => set({ [k]: v } as Partial<Look>);
  const file = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function upload(f: File) {
    const contentType = f.type === 'image/svg+xml' || f.name.toLowerCase().endsWith('.svg')
      ? 'image/svg+xml'
      : f.type === 'image/png' || f.name.toLowerCase().endsWith('.png') ? 'image/png' : null;
    if (!contentType) { toast('SVG or PNG only.', 'bad'); return; }
    if (f.size > LOGO_LIMIT) { toast(`Logo is ${bytes(f.size)}; the limit is 50 KB.`, 'bad'); return; }
    setUploading(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).replace(/^data:[^;]+;base64,/, ''));
        r.onerror = () => reject(new Error('File could not be read.'));
        r.readAsDataURL(f);
      });
      const asset = await bannerApi.uploadLogo(contentType, dataBase64);
      set({ logoAssetId: asset.id });
      onLogo(asset.url);
      toast(`Logo uploaded, ${bytes(asset.size)}.`, 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setUploading(false);
      if (file.current) file.current.value = '';
    }
  }

  const logoSrc = look.logoAssetId ? (logoUrl ?? `/v1/assets/${look.logoAssetId}`) : null;

  return (
    <Card title="Look">
      <div className="ck-colours">
        <Colour label={LOOK_LABEL.brand} value={look.brand} readOnly={readOnly} onChange={colour('brand')} />
        <Colour label={LOOK_LABEL.button} value={look.button} readOnly={readOnly} onChange={colour('button')}>
          <Contrast fg={look.buttonText} bg={look.button} against="button text on button" />
        </Colour>
        <Colour label={LOOK_LABEL.buttonText} value={look.buttonText} readOnly={readOnly} onChange={colour('buttonText')} />
        <Colour label={LOOK_LABEL.surface} value={look.surface} readOnly={readOnly} onChange={colour('surface')} />
        <Colour label={LOOK_LABEL.text} value={look.text} readOnly={readOnly} onChange={colour('text')}>
          <Contrast fg={look.text} bg={look.surface} against="text on surface" />
        </Colour>
        <Colour label={LOOK_LABEL.border} value={look.border} readOnly={readOnly} onChange={colour('border')} />
      </div>

      <div className="row">
        <Field label="Font">
          <select value={look.font} disabled={readOnly} onChange={(e) => set({ font: e.target.value as Look['font'] })}>
            {(Object.keys(FONT_LABEL) as Look['font'][]).map((k) => <option key={k} value={k}>{FONT_LABEL[k]}</option>)}
          </select>
        </Field>
        <Field label="Base size" help="13 to 18 px">
          <input
            type="number" min={13} max={18} step={1} value={look.baseSize} readOnly={readOnly}
            onChange={(e) => { const n = Number(e.target.value); if (Number.isInteger(n)) set({ baseSize: Math.min(18, Math.max(13, n)) }); }}
          />
        </Field>
        <Field label="Corner radius">
          <select value={look.radius} disabled={readOnly} onChange={(e) => set({ radius: e.target.value as Look['radius'] })}>
            {(Object.keys(RADIUS_LABEL) as Look['radius'][]).map((k) => <option key={k} value={k}>{RADIUS_LABEL[k]}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Logo" help="SVG or PNG up to 50 KB, shown in the banner header.">
        {logoSrc ? (
          <div className="ck-logo">
            <img src={logoSrc} alt="Logo" />
            {!readOnly && <button className="small" onClick={() => { set({ logoAssetId: null }); onLogo(null); }}>Remove</button>}
          </div>
        ) : (
          <input
            ref={file} type="file" accept=".svg,.png,image/svg+xml,image/png" disabled={readOnly || uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
        )}
        {uploading && <div className="help">Uploading…</div>}
      </Field>

      <label className="ck-check">
        <input type="checkbox" checked={look.poweredBy} disabled={readOnly} onChange={(e) => set({ poweredBy: e.target.checked })} />
        Show "Data protection and consent by Wekalp"
      </label>

      <details className="ck-details">
        <summary>Custom CSS</summary>
        <div className="note warn" style={{ margin: '8px 0' }}>Applied inside the banner after the built-in styles. Not contrast-checked.</div>
        <textarea
          className="mono" rows={8} value={look.customCss} maxLength={8000} readOnly={readOnly} spellCheck={false}
          placeholder=".wk-banner { }"
          onChange={(e) => set({ customCss: e.target.value })}
        />
        {look.customCss && <div className="help">{look.customCss.length} of 8000</div>}
      </details>
    </Card>
  );
}
